import { useEffect, useMemo, useState } from 'react';
import type { Editor } from '@tiptap/react';
import type { SelectionList } from 'types/textBuildingBlocks';
import { plainToContent } from './buildingBlockContent';
import { SELECTION_LIST_PLACEHOLDER } from './placeholderTypes';

interface Params {
  editor: Editor;
  activeListPos: number | null;
  selectionLists: SelectionList[];
  onResolved?: (fromPos: number) => boolean;
}

export function useSelectionListResolution({
  editor,
  activeListPos,
  selectionLists,
  onResolved,
}: Params) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setSelected(new Set());
    setActiveIndex(0);
  }, [activeListPos]);

  const node = activeListPos === null ? null : editor.state.doc.nodeAt(activeListPos);
  const active =
    node && node.type.name === SELECTION_LIST_PLACEHOLDER
      ? { name: node.attrs.name as string, size: node.nodeSize }
      : null;

  const list = active ? (selectionLists.find((entry) => entry.name === active.name) ?? null) : null;

  const isOpen = active !== null;
  const available = list !== null;
  const multiSelect = list?.multiSelect ?? false;
  const options = useMemo(() => list?.options ?? [], [list]);
  const name = active?.name ?? '';
  const rowCount = options.length;

  const reset = () => {
    setSelected(new Set());
    setActiveIndex(0);
  };

  const applyReplacement = (replacement: string) => {
    if (activeListPos === null || !active) return;
    const from = activeListPos;
    const to = activeListPos + active.size;
    const content = plainToContent(replacement);
    const chain = editor.chain().focus();

    if (content.length === 0) chain.deleteRange({ from, to });
    else chain.insertContentAt({ from, to }, content);
    chain.run();
    reset();
    onResolved?.(from);
  };

  const chooseSingle = (index: number) => {
    if (!list || index < 0 || index >= list.options.length) return;
    applyReplacement(list.options[index]);
  };

  const toggle = (index: number) => {
    if (!list || index < 0 || index >= list.options.length) return;
    setSelected((prev) => {
      const nextSet = new Set(prev);
      if (nextSet.has(index)) nextSet.delete(index);
      else nextSet.add(index);
      return nextSet;
    });
  };

  const commitMulti = () => {
    if (!list) return;
    const chosen = [...selected].sort((a, b) => a - b).map((index) => list.options[index]);
    applyReplacement(chosen.join(list.separator));
  };

  const noSelection = () => applyReplacement('');

  const cancel = () => {
    if (activeListPos === null || !active) return;
    const from = activeListPos;
    reset();
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.setNodeAttribute(from, 'skipped', true);
        return true;
      })
      .setTextSelection(from + active.size)
      .run();
  };

  const keepAndAdvance = () => {
    if (activeListPos === null || !active) return;
    const after = activeListPos + active.size;
    if (!onResolved?.(after)) {
      editor.chain().focus().setTextSelection(after).run();
    }
  };

  const onOptionClick = (index: number) => {
    if (multiSelect) toggle(index);
    else chooseSingle(index);
  };

  const onDigit = (digit: number) => {
    if (digit === 0) {
      noSelection();
      return;
    }
    onOptionClick(digit - 1);
  };

  // Multi-select lists add the confirm button as a final navigable position (index === rowCount),
  // so checking the last option lands on it instead of wrapping back to the top.
  const navigableCount = multiSelect ? rowCount + 1 : rowCount;
  const isCommitActive = multiSelect && activeIndex === rowCount;

  const moveActive = (delta: number) => {
    if (navigableCount === 0) return;
    setActiveIndex((index) => (index + delta + navigableCount) % navigableCount);
  };

  const onKeyDown = (event: KeyboardEvent): boolean => {
    if (!isOpen) return false;
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
      return true;
    }
    if (event.key === 'Tab') {
      if (multiSelect && selected.size > 0) {
        event.preventDefault();
        commitMulti();
        return true;
      }
      return false;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(1);
      return true;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(-1);
      return true;
    }
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      onDigit(Number(event.key));
      return true;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (multiSelect) {
        if (isCommitActive || selected.size > 0) commitMulti();
        else keepAndAdvance();
      } else {
        chooseSingle(activeIndex);
      }
      return true;
    }
    if (event.key === ' ') {
      event.preventDefault();
      if (multiSelect) {
        if (isCommitActive) commitMulti();
        else {
          toggle(activeIndex);
          moveActive(1);
        }
      } else {
        chooseSingle(activeIndex);
      }
      return true;
    }
    // The popup owns the field while open: swallow any other character or delete key so it can't
    // replace or delete the node-selected chip underneath. Modifier combos (Cmd/Ctrl/Alt) are
    // system shortcuts like Copy/Paste/Select-All, so let them pass through untouched.
    const hasModifier = event.metaKey || event.ctrlKey || event.altKey;
    if (
      !hasModifier &&
      (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete')
    ) {
      event.preventDefault();
      return true;
    }
    return false;
  };

  return {
    isOpen,
    name,
    options,
    multiSelect,
    available,
    selected,
    activeIndex,
    anchorPos: isOpen ? activeListPos : null,
    onKeyDown,
    onOptionClick,
    onNoSelection: noSelection,
    onCommit: commitMulti,
    onHover: setActiveIndex,
    cancel,
  };
}
